import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ExtensionIcon from "@mui/icons-material/Extension";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import SlidingPlayMain from "../../../admin/story/SlidingPlayMain";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import {
    getImageUrl,
    getTemplateGrid,
    getTemplateStartingLayout,
    loadScreenshotSliding,
} from "../lib/screenshotApi";
import { isScreenshotAccessExpired } from "../lib/screenshotAccess";
import { FEATURE_GRAPHIC_PATH } from "../routes";
import { getScreenshotScene, isScreenshotSceneConfigured } from "../screenshotScenes";

/** Google Play Feature Graphic canvas size. */
export const FEATURE_GRAPHIC_W = 1024;
export const FEATURE_GRAPHIC_H = 500;

/** Instagram square feature graphic canvas size. */
export const INSTAGRAM_FEATURE_GRAPHIC_SIZE = 1080;

const SAVE_BAR_H = 64;
const FEATURE_GRAPHIC_FILE_NAME = "sliding-puzzle-quest-feature-graphic-1024x500.png";

/** Same Funny kitchen / waffle puzzle as `/screenshot/05`. */
const FEATURE_GRAPHIC_SCENE_SLUG = "05";

const TITLE_STACK =
    '"Segoe UI", "Arial Black", "Helvetica Neue", Impact, sans-serif';
const TAGLINE_STACK =
    'Georgia, "Palatino Linotype", "Book Antiqua", Palatino, "Times New Roman", serif';

const FEATURE_ITEMS = [
    { label: "Daily Challenges", Icon: TimerOutlinedIcon },
    { label: "Play Together", Icon: EmojiEventsIcon },
    { label: "1000+ Puzzles", Icon: ExtensionIcon },
];

const FEATURE_ICON_HIGHLIGHT_MS = 2000;
const FEATURE_ICON_INTRO_PAUSE_MS = 3000;
/** Recording HUD starts at 00:02:01 when the first icon highlights. */
const FEATURE_TIMER_START_MS = (2 * 60 + 1) * 1000;

function formatFeatureTimer(ms) {
    const totalSec = Math.max(0, Math.floor(Number(ms) / 1000));
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    return [hours, minutes, seconds]
        .map((n) => String(n).padStart(2, "0"))
        .join(":");
}

const FLOATING_TILE_OFFSETS = [
    { top: 28, left: -10, rotate: -18, size: 52, opacity: 0.98 },
    { top: 140, left: -16, rotate: 14, size: 44, opacity: 0.92 },
    { top: 218, left: 14, rotate: -8, size: 38, opacity: 0.88 },
    { top: 16, right: -6, rotate: 22, size: 46, opacity: 0.95 },
    { top: 186, right: -8, rotate: -16, size: 40, opacity: 0.9 },
];

const PARTICLES = [
    { top: "12%", left: "58%", size: 3, delay: "0s" },
    { top: "22%", left: "72%", size: 2, delay: "0.4s" },
    { top: "38%", left: "64%", size: 4, delay: "1.1s" },
    { top: "55%", left: "80%", size: 2, delay: "0.7s" },
    { top: "68%", left: "55%", size: 3, delay: "1.6s" },
    { top: "18%", left: "88%", size: 2, delay: "2s" },
    { top: "78%", left: "70%", size: 3, delay: "0.2s" },
    { top: "44%", left: "92%", size: 2, delay: "1.3s" },
    { top: "8%", left: "42%", size: 2, delay: "0.9s" },
    { top: "86%", left: "48%", size: 3, delay: "1.8s" },
];

function tileBackgroundPosition(sliceIndex, gridSize) {
    const gs = Math.max(1, Number(gridSize) || 1);
    const col = sliceIndex % gs;
    const row = Math.floor(sliceIndex / gs);
    if (gs <= 1) return "0% 0%";
    return `${(col / (gs - 1)) * 100}% ${(row / (gs - 1)) * 100}%`;
}

function pickFloatingSliceIndices(layout, gridSize, count) {
    const gs = Number(gridSize) || 0;
    const emptyIdx = gs > 0 ? gs * gs - 1 : -1;
    const unique = [];
    const seen = new Set();
    for (const raw of layout ?? []) {
        const slice = Number(raw);
        if (!Number.isFinite(slice) || slice === emptyIdx || seen.has(slice)) continue;
        seen.add(slice);
        unique.push(slice);
        if (unique.length >= count) break;
    }
    return unique;
}

function FloatingTile({ imageUrl, gridSize, sliceIndex, style }) {
    const size = style.size ?? 64;
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                width: size,
                height: size,
                top: style.top,
                left: style.left,
                right: style.right,
                borderRadius: "10px",
                overflow: "hidden",
                opacity: style.opacity ?? 1,
                transform: `rotate(${style.rotate ?? 0}deg)`,
                backgroundImage: `url("${imageUrl}")`,
                backgroundSize: `${gridSize * 100}% ${gridSize * 100}%`,
                backgroundPosition: tileBackgroundPosition(sliceIndex, gridSize),
                backgroundRepeat: "no-repeat",
                boxShadow:
                    "0 0 0 2px rgba(14, 165, 233, 0.55), 0 0 16px rgba(125, 211, 252, 0.45), 0 10px 22px rgba(15, 23, 42, 0.18)",
                zIndex: 4,
                pointerEvents: "none",
            }}
        />
    );
}

function FeatureGraphicLeftCopy({
    cycleIcons = false,
    showIcons = true,
    activeIndex = 0,
}) {
    return (
        <Box
            sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 500,
                zIndex: 5,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                pl: 4.5,
                pr: 2,
                boxSizing: "border-box",
                pointerEvents: "none",
                textAlign: "left",
            }}
        >
            <Typography
                component="h1"
                sx={{
                    m: 0,
                    fontFamily: TITLE_STACK,
                    fontWeight: 900,
                    fontSize: 46,
                    lineHeight: 0.92,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                    background:
                        "linear-gradient(180deg, #38bdf8 0%, #0ea5e9 42%, #0284c7 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    filter:
                        "drop-shadow(0 1px 0 rgba(255,255,255,0.95)) drop-shadow(0 2px 6px rgba(14, 165, 233, 0.22))",
                    maxWidth: 460,
                    userSelect: "none",
                }}
            >
                Sliding
                <Box component="br" />
                Puzzle Quest
            </Typography>

            <Typography
                component="p"
                sx={{
                    m: 0,
                    mt: 1.75,
                    fontFamily: TAGLINE_STACK,
                    fontStyle: "italic",
                    fontWeight: 500,
                    fontSize: 15,
                    lineHeight: 1.35,
                    letterSpacing: "0.06em",
                    color: "#64748b",
                    textShadow: "0 1px 0 rgba(255,255,255,0.8)",
                    maxWidth: 420,
                    userSelect: "none",
                }}
            >
                MASTER THE CHAOS. PIECE TOGETHER THE STORY.
            </Typography>

            <Stack
                direction="row"
                spacing={2.25}
                sx={{
                    mt: 3.25,
                    mb: 0.5,
                    opacity: cycleIcons && !showIcons ? 0 : 1,
                    visibility: cycleIcons && !showIcons ? "hidden" : "visible",
                    transition: "opacity 0.35s ease",
                }}
            >
                {FEATURE_ITEMS.map(({ label, Icon }, index) => {
                    const highlighted = cycleIcons && index === activeIndex;
                    const dimmed = cycleIcons && !highlighted;
                    return (
                    <Stack
                        key={label}
                        alignItems="center"
                        spacing={0.75}
                        sx={{
                            width: 108,
                            opacity: dimmed ? 0.42 : 1,
                            transform: highlighted ? "translateY(-4px)" : "none",
                            transition: "opacity 0.35s ease, transform 0.35s ease",
                        }}
                    >
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: highlighted
                                    ? "linear-gradient(145deg, #e0f2fe 0%, #38bdf8 48%, #0284c7 100%)"
                                    : "linear-gradient(145deg, #ffffff 0%, #e0f2fe 55%, #bae6fd 100%)",
                                border: highlighted
                                    ? "1.5px solid #0284c7"
                                    : "1px solid rgba(14, 165, 233, 0.35)",
                                boxShadow: highlighted
                                    ? "0 0 18px rgba(14,165,233,0.45), 0 6px 14px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.85)"
                                    : "0 4px 12px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
                                transform: highlighted ? "scale(1.22)" : "scale(1)",
                                transition:
                                    "transform 0.35s ease, box-shadow 0.35s ease, background 0.35s ease, border-color 0.35s ease",
                            }}
                        >
                            <Icon
                                sx={{
                                    fontSize: highlighted ? 24 : 22,
                                    color: highlighted ? "#ffffff" : "#0369a1",
                                    filter: highlighted
                                        ? "drop-shadow(0 1px 2px rgba(2,6,23,0.25))"
                                        : "none",
                                    transition: "font-size 0.35s ease, filter 0.35s ease",
                                }}
                            />
                        </Box>
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: TITLE_STACK,
                                fontWeight: highlighted ? 800 : 700,
                                fontSize: 11,
                                lineHeight: 1.2,
                                letterSpacing: "0.02em",
                                color: highlighted
                                    ? "#0369a1"
                                    : "#64748b",
                                textAlign: "center",
                                textShadow: "none",
                                userSelect: "none",
                                transition: "color 0.35s ease, text-shadow 0.35s ease",
                            }}
                        >
                            {label}
                        </Typography>
                    </Stack>
                    );
                })}
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Box
                    sx={{
                        px: 2.75,
                        py: 1.1,
                        borderRadius: 999,
                        background:
                            "linear-gradient(180deg, #7dd3fc 0%, #0ea5e9 45%, #0369a1 100%)",
                        border: "1.5px solid rgba(226, 232, 240, 0.75)",
                        boxShadow:
                            "0 6px 16px rgba(14, 165, 233, 0.28), inset 0 1px 0 rgba(255,255,255,0.55)",
                        minWidth: 118,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: TITLE_STACK,
                            fontWeight: 800,
                            fontSize: 13,
                            letterSpacing: "0.1em",
                            color: "#f0f9ff",
                            textShadow: "0 1px 2px rgba(2,6,23,0.45)",
                            userSelect: "none",
                        }}
                    >
                        PLAY NOW
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
}

/**
 * Google Play Feature Graphic draft — 1024×500 landscape capture page.
 * Puzzle matches Sliding screenshot/05; left side reserved for title copy.
 * `endingCapture` — letterbox into 1920×1080 (contain).
 * `fillCapture` — fit the graphic in the viewport for OBS window capture (contain).
 */
export default function FeatureGraphicPage({ endingCapture = false, fillCapture = false }) {
    const captureMode = endingCapture || fillCapture;
    const scene = getScreenshotScene(FEATURE_GRAPHIC_SCENE_SLUG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [showFeatureIcons, setShowFeatureIcons] = useState(!fillCapture);
    const [featureActiveIndex, setFeatureActiveIndex] = useState(0);
    const [featureTimerMs, setFeatureTimerMs] = useState(FEATURE_TIMER_START_MS);
    const [featureTimerRunning, setFeatureTimerRunning] = useState(false);
    const frameRef = useRef(null);

    const expired = isScreenshotAccessExpired();

    useEffect(() => {
        if (expired) {
            setLoading(false);
            return undefined;
        }
        if (!scene || !isScreenshotSceneConfigured(scene)) {
            setError("Feature graphic scene is not configured");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await loadScreenshotSliding(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load puzzle");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [expired, scene]);

    const imageUrl = useMemo(() => getImageUrl(group?.story), [group]);
    const gridSize = useMemo(() => getTemplateGrid(template), [template]);
    const startingLayout = useMemo(
        () => getTemplateStartingLayout(template),
        [template],
    );
    const floatingSlices = useMemo(
        () => pickFloatingSliceIndices(startingLayout, gridSize, FLOATING_TILE_OFFSETS.length),
        [startingLayout, gridSize],
    );

    useEffect(() => {
        if (expired || loading || error || !imageUrl || !startingLayout.length) {
            markScreenshotNotReady();
            return undefined;
        }
        signalScreenshotSceneReady({
            path: FEATURE_GRAPHIC_PATH,
            slug: "feature-graphic",
        });
        return undefined;
    }, [expired, loading, error, imageUrl, startingLayout.length]);

    useEffect(() => {
        const updateScale = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            if (fillCapture || endingCapture) {
                // Fit the whole 1024×500 graphic in the window (OBS / 1080p popup).
                setPreviewScale(Math.min(vw / FEATURE_GRAPHIC_W, vh / FEATURE_GRAPHIC_H));
                return;
            }
            const padding = 32;
            const availableW = vw - padding;
            const availableH = vh - SAVE_BAR_H - padding;
            setPreviewScale(
                Math.min(1, availableW / FEATURE_GRAPHIC_W, availableH / FEATURE_GRAPHIC_H),
            );
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, [endingCapture, fillCapture]);

    useEffect(() => {
        const previousHtmlOverflow = document.documentElement.style.overflow;
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlBg = document.documentElement.style.backgroundColor;
        const previousBodyBg = document.body.style.backgroundColor;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        document.documentElement.style.backgroundColor = "#ffffff";
        document.body.style.backgroundColor = "#ffffff";
        return () => {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.backgroundColor = previousHtmlBg;
            document.body.style.backgroundColor = previousBodyBg;
        };
    }, []);

    const handleSave = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;

        setSaving(true);
        setSaveError(null);
        const previousTransform = node.style.transform;
        node.style.transform = "none";

        try {
            // Let layout settle at native 1024×500 before capture.
            await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
            const dataUrl = await toPng(node, {
                width: FEATURE_GRAPHIC_W,
                height: FEATURE_GRAPHIC_H,
                pixelRatio: 1,
                cacheBust: true,
                skipFonts: true,
                style: {
                    transform: "none",
                    transformOrigin: "top left",
                },
            });
            const link = document.createElement("a");
            link.download = FEATURE_GRAPHIC_FILE_NAME;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            setSaveError(e?.message || "Failed to save image");
        } finally {
            node.style.transform = previousTransform;
            setSaving(false);
        }
    }, [saving]);

    const ready = Boolean(imageUrl && gridSize > 0 && startingLayout.length);
    const recordingReady = fillCapture && !loading && !error && ready;

    useEffect(() => {
        if (!fillCapture) {
            setShowFeatureIcons(true);
            setFeatureTimerRunning(false);
            return undefined;
        }
        if (!recordingReady) return undefined;

        setShowFeatureIcons(false);
        setFeatureActiveIndex(0);
        setFeatureTimerMs(FEATURE_TIMER_START_MS);
        setFeatureTimerRunning(false);

        const startId = window.setTimeout(() => {
            setShowFeatureIcons(true);
            setFeatureActiveIndex(0);
            setFeatureTimerRunning(true);
        }, FEATURE_ICON_INTRO_PAUSE_MS);

        return () => window.clearTimeout(startId);
    }, [fillCapture, recordingReady]);

    useEffect(() => {
        if (!fillCapture || !featureTimerRunning) return undefined;
        const id = window.setInterval(() => {
            setFeatureActiveIndex((prev) => (prev + 1) % FEATURE_ITEMS.length);
        }, FEATURE_ICON_HIGHLIGHT_MS);
        return () => window.clearInterval(id);
    }, [fillCapture, featureTimerRunning]);

    useEffect(() => {
        if (!featureTimerRunning) return undefined;
        const startedAt = Date.now();
        const id = window.setInterval(() => {
            setFeatureTimerMs(FEATURE_TIMER_START_MS + (Date.now() - startedAt));
        }, 200);
        return () => window.clearInterval(id);
    }, [featureTimerRunning]);

    if (expired) {
        return <ScreenshotExpiredPage />;
    }

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#ffffff",
                overflow: "hidden",
                zIndex: 1,
                gap: captureMode ? 0 : 1.5,
                pb: captureMode ? 0 : `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    ...(captureMode
                        ? {
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              width: FEATURE_GRAPHIC_W,
                              height: FEATURE_GRAPHIC_H,
                              transform: `translate(-50%, -50%) scale(${previewScale})`,
                              transformOrigin: "center center",
                              overflow: "hidden",
                              flexShrink: 0,
                          }
                        : {
                              width: Math.round(FEATURE_GRAPHIC_W * previewScale),
                              height: Math.round(FEATURE_GRAPHIC_H * previewScale),
                              position: "relative",
                              overflow: "hidden",
                              flexShrink: 0,
                          }),
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="sliding-feature-graphic"
                    sx={{
                        width: FEATURE_GRAPHIC_W,
                        height: FEATURE_GRAPHIC_H,
                        ...(captureMode
                            ? {}
                            : {
                                  transform: `scale(${previewScale})`,
                                  transformOrigin: "top left",
                              }),
                        position: "relative",
                        overflow: "hidden",
                        bgcolor: "#ffffff",
                        boxShadow: captureMode
                            ? "none"
                            : "0 0 0 1px rgba(148, 163, 184, 0.35)",
                    }}
                >
                    {/* Left: light studio / title negative space */}
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: `
                                radial-gradient(ellipse 55% 80% at 18% 50%, rgba(224, 242, 254, 0.9) 0%, transparent 70%),
                                radial-gradient(ellipse 40% 60% at 88% 42%, rgba(240, 249, 255, 0.95) 0%, transparent 65%),
                                linear-gradient(105deg, #ffffff 0%, #f0f9ff 42%, #e0f2fe 72%, #f8fafc 100%)
                            `,
                        }}
                    />
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: "48%",
                            backgroundImage: `
                                linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(240,249,255,0.35) 55%, rgba(255,255,255,0.2) 100%),
                                radial-gradient(ellipse at 30% 40%, rgba(14,165,233,0.06), transparent 55%),
                                repeating-linear-gradient(
                                    90deg,
                                    transparent 0,
                                    transparent 48px,
                                    rgba(148,163,184,0.08) 48px,
                                    rgba(148,163,184,0.08) 49px
                                )
                            `,
                            filter: "blur(0.4px)",
                            opacity: 0.95,
                            pointerEvents: "none",
                        }}
                    />
                    {/* Soft out-of-focus hall depth behind left copy */}
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            left: "4%",
                            bottom: "6%",
                            width: 200,
                            height: 140,
                            borderRadius: "40% 40% 12% 12%",
                            background:
                                "linear-gradient(180deg, rgba(186,230,253,0.55) 0%, rgba(241,245,249,0.2) 100%)",
                            filter: "blur(18px)",
                            opacity: 0.7,
                            zIndex: 1,
                        }}
                    />
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            left: "18%",
                            bottom: "8%",
                            width: 150,
                            height: 110,
                            borderRadius: "35% 35% 10% 10%",
                            background:
                                "linear-gradient(180deg, rgba(125,211,252,0.35) 0%, rgba(241,245,249,0.15) 100%)",
                            filter: "blur(22px)",
                            opacity: 0.55,
                            zIndex: 1,
                        }}
                    />

                    <FeatureGraphicLeftCopy
                        cycleIcons={fillCapture}
                        showIcons={showFeatureIcons}
                        activeIndex={featureActiveIndex}
                    />

                    {loading ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={36} sx={{ color: "#38bdf8" }} />
                        </Box>
                    ) : error ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 4,
                            }}
                        >
                            <Typography color="error" align="center">
                                {error}
                            </Typography>
                        </Box>
                    ) : ready ? (
                        <>
                            {/* Holographic connector lines */}
                            <Box
                                component="svg"
                                aria-hidden
                                viewBox={`0 0 ${FEATURE_GRAPHIC_W} ${FEATURE_GRAPHIC_H}`}
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    pointerEvents: "none",
                                    zIndex: 3,
                                }}
                            >
                                <defs>
                                    <linearGradient id="fgHolo" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.05" />
                                        <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.85" />
                                        <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.15" />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M 520 90 C 580 120, 620 160, 690 150"
                                    fill="none"
                                    stroke="url(#fgHolo)"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M 500 280 C 560 250, 600 300, 660 320"
                                    fill="none"
                                    stroke="url(#fgHolo)"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M 780 70 C 820 110, 860 90, 900 130"
                                    fill="none"
                                    stroke="url(#fgHolo)"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M 760 390 C 800 360, 850 400, 910 370"
                                    fill="none"
                                    stroke="url(#fgHolo)"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </Box>

                            {/* Right: tilted board in the right half — left-anchored so it doesn't drift. */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: 548,
                                    top: "46%",
                                    width: 304,
                                    height: 304,
                                    transform: "translateY(-50%) rotate(-7deg)",
                                    transformOrigin: "center center",
                                    zIndex: 2,
                                }}
                            >
                                {/* Glow aura */}
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: "absolute",
                                        inset: -12,
                                        borderRadius: 4,
                                        background:
                                            "radial-gradient(circle at 50% 45%, rgba(56,189,248,0.22) 0%, rgba(186,230,253,0.18) 45%, transparent 70%)",
                                        filter: "blur(8px)",
                                        zIndex: 0,
                                    }}
                                />

                                {/* Timer chrome */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: -22,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        px: 2.25,
                                        py: 0.75,
                                        borderRadius: 999,
                                        bgcolor: "rgba(255, 255, 255, 0.94)",
                                        border: "1px solid rgba(14, 165, 233, 0.45)",
                                        boxShadow:
                                            "0 6px 16px rgba(15, 23, 42, 0.12), inset 0 0 8px rgba(186,230,253,0.45)",
                                        zIndex: 5,
                                        pointerEvents: "none",
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontFamily:
                                                '"JetBrains Mono", "SF Mono", "Consolas", monospace',
                                            fontWeight: 700,
                                            fontSize: 18,
                                            letterSpacing: "0.08em",
                                            color: "#0369a1",
                                            textShadow: "none",
                                            lineHeight: 1,
                                            userSelect: "none",
                                        }}
                                    >
                                        {fillCapture
                                            ? formatFeatureTimer(featureTimerMs)
                                            : "00:34.12"}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: "18px",
                                        p: "10px",
                                        background:
                                            "linear-gradient(145deg, rgba(186,230,253,0.95) 0%, #ffffff 42%, rgba(224,242,254,0.95) 100%)",
                                        boxShadow:
                                            "0 0 0 1px rgba(14,165,233,0.28), 0 18px 36px rgba(15,23,42,0.16), 0 0 32px rgba(125,211,252,0.28)",
                                        zIndex: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "12px",
                                            overflow: "hidden",
                                            bgcolor: "#f8fafc",
                                            boxShadow: "inset 0 0 0 2px rgba(14,165,233,0.28)",
                                        }}
                                    >
                                        <SlidingPlayMain
                                            imageUrl={imageUrl}
                                            gridSize={gridSize}
                                            startingLayout={startingLayout}
                                            hideCompletedCaption
                                            fillParent
                                            readOnly
                                            playMoveSound={false}
                                            showMatchCelebrations={false}
                                            gridGutterColor="#7dd3fc"
                                            gridBorderColor="#38bdf8"
                                        />
                                    </Box>
                                </Box>

                                {floatingSlices.map((sliceIndex, i) => (
                                    <FloatingTile
                                        key={`float-${sliceIndex}-${i}`}
                                        imageUrl={imageUrl}
                                        gridSize={gridSize}
                                        sliceIndex={sliceIndex}
                                        style={FLOATING_TILE_OFFSETS[i]}
                                    />
                                ))}
                            </Box>

                            {/* Sparkle particles */}
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    pointerEvents: "none",
                                    zIndex: 6,
                                    "@keyframes fgSparkle": {
                                        "0%, 100%": { opacity: 0.15, transform: "scale(0.7)" },
                                        "50%": { opacity: 1, transform: "scale(1.15)" },
                                    },
                                }}
                            >
                                {PARTICLES.map((p, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            position: "absolute",
                                            top: p.top,
                                            left: p.left,
                                            width: p.size,
                                            height: p.size,
                                            borderRadius: "50%",
                                            bgcolor: i % 2 === 0 ? "#38bdf8" : "#a5b4fc",
                                            boxShadow: `0 0 ${p.size * 3}px ${
                                                i % 2 === 0 ? "rgba(56,189,248,0.45)" : "rgba(165,180,252,0.4)"
                                            }`,
                                            animation: `fgSparkle ${2.4 + (i % 3) * 0.4}s ease-in-out ${p.delay} infinite`,
                                        }}
                                    />
                                ))}
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="text.secondary">Puzzle image missing.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {!captureMode ? (
            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    bgcolor: "rgba(248, 250, 252, 0.96)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.35)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <SaveAltIcon />
                        )
                    }
                    onClick={handleSave}
                    disabled={!ready || saving || loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    {saving ? "Saving…" : "Save 1024×500 PNG"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
            ) : null}
        </Box>
    );
}
